import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'

import { TicketDetailModel, AbleTimeModel, initList } from '@/components/purchase/components/parkinglot/model'
import type { PaymentMethodList, PostMessage } from '@/components/purchase/components/sharedParkinglot/viewModel'

import collect from '@/utils/daangnEvents/purchase'
import paymentEvents from '@/utils/gaEvents/paymentEvents'

import Shared from '@/shared'
import type { Dispatch, LegacyRef, SetStateAction } from 'react'

import { PARKINGLOT_TYPE } from '@/enums/detail'
import { PLATFORM_TYPE } from '@/enums/purchase'

import { requestParkinglotPayment } from '@/api/client/purchase/index'
import { ClientRequestError } from '@/lib/axiosClient'

export interface PurchaseViewModel {
  texts: object
  states: {
    step: number
    isPurchase: boolean
    isLoading: boolean
    isErrorToast: boolean
    isComplete: boolean
    isOpen: boolean
    isToastOpen: boolean
    isInComplete: boolean
    frameUrl: string
    toastText: string
    ticketSeq: number
    windowHeight: number
    contentHeight: number
    headerRef: LegacyRef<HTMLDivElement> | null
    footerRef: LegacyRef<HTMLDivElement> | null
    paymentList: PaymentMethodList
  }
  setters: {
    setIsLoading: Dispatch<SetStateAction<boolean>>
    setIsComplete: Dispatch<SetStateAction<boolean>>
    setIsInComplete: Dispatch<SetStateAction<boolean>>
    setIsToastOpen: Dispatch<SetStateAction<boolean>>
    setIsErrorToast: Dispatch<SetStateAction<boolean>>
  }
  handlers: {
    onClickRePayment: () => void
    onClickPayment: () => void
    onClosePaymentModal: () => void
    onClickShowTicketBtn: () => void
    radioHandler: (event: React.ChangeEvent<HTMLInputElement>) => void
    onClickHeaderBackBtn: () => void
  }
}

const useParkinglotPurchaseViewModel = (
  data: TicketDetailModel,
  carNum: string,
  enteringTime: AbleTimeModel | null,
  token: string,
  authCode: number,
  setIsConfirmOpen: Dispatch<SetStateAction<boolean>>
): PurchaseViewModel => {
  const router = useRouter()

  const headerRef = useRef<HTMLDivElement | null>(null)
  const footerRef = useRef<HTMLDivElement | null>(null)

  const { purchaseStep: step, setPurchaseStep: setStep } = Shared.States.usePurchaseStep()
  const { isPurchase, setIsPurchase } = Shared.States.useIsPurchase()

  const [paymentList, setPaymentList] = useState<PaymentMethodList>(initList)

  const [frameUrl, setFrameUrl] = useState('')
  const [guestCode, setGuestCode] = useState('')
  const [toastText, setToastText] = useState('')

  const [windowHeight, setWindowHeight] = useState(0)
  const [ticketSeq, setTicketSeq] = useState(0)
  const [contentHeight, setContentHeight] = useState(0)

  const [isErrorToast, setIsErrorToast] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const [isInComplete, setIsInComplete] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [isToastOpen, setIsToastOpen] = useState(false)

  //결제수단 선택
  const radioHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = event.target.value
    const updatedRadioList = paymentList.map((radio) =>
      radio.value === selectedValue ? { ...radio, checked: true } : { ...radio, checked: false }
    )
    setPaymentList(updatedRadioList)
  }

  const receiveMessage = useCallback((e: MessageEvent<PostMessage>) => {
    if (e.origin !== process.env.NEXT_PUBLIC_MODU_API_HOST) return

    if (e.data.result === 'success' && e.data.couSeq && e.data.guestCode) {
      setFrameUrl('')
      setIsComplete(true)
      setIsOpen(false)
      paymentEvents.partner.completePayment(e.data.couSeq)
      setTicketSeq(e.data.couSeq)
      setGuestCode(e.data.guestCode)
      collect()
    }
    if (e.data.result === 'failure') {
      setFrameUrl('')
      setIsInComplete(true)
    }
  }, [])

  const onClickPayment = async () => {
    paymentEvents.partner.choosePaymentMethod()
    const payment = paymentList.find((el) => el.checked === true)
    if (payment && enteringTime && payment.id && token && authCode) {
      if (!isMobile) setIsOpen((prev) => !prev)
      const pathVar = {
        pgType: payment.id
      }
      const payload = {
        carNum: `${carNum}`,
        point: 0,
        price: data.price,
        totalPrice: data.price,
        couponSeq: data.couponSeq,
        predictBeginTime: new Date(enteringTime.predictBeginTime),
        predictEndTime: new Date(enteringTime.predictEndTime),
        authCodeSeq: authCode,
        platform: PLATFORM_TYPE.WEB
      }
      const auth = {
        token: token
      }
      try {
        const { data } = await requestParkinglotPayment(pathVar, payload, auth)
        const url = data.redirectUrl
        if (payment.id === 'naverpay') {
          router.replace(url)
        } else {
          setFrameUrl(url)
        }
        window.addEventListener('message', receiveMessage, false)
      } catch (err: unknown) {
        if (err instanceof ClientRequestError) {
          setFrameUrl('')
          setIsErrorToast(true)
          setToastText(err.message)
        } else {
          throw err
        }
      }
    }
  }

  const onClickRePayment = () => {
    setIsOpen(false)
    setIsInComplete(false)
    setFrameUrl('')
    setPaymentList([...initList])
  }

  const onClosePaymentModal = () => {
    setIsOpen((prev) => !prev)
  }

  const onClickShowTicketBtn = () => {
    setIsPurchase(false)
    setStep(0)
    router.replace(
      `/my-ticket/${ticketSeq}?type=${PARKINGLOT_TYPE.PARKINGLOT.toLowerCase()}&guestCode=${guestCode}&token=${token}`
    )
  }
  const onClickHeaderBackBtn = () => {
    setStep(3)
    setIsConfirmOpen((prev) => !prev)
  }

  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight)
    }

    handleResize()

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('message', receiveMessage)
    }
  }, [])

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight ?? 0
    const footerHeight = footerRef.current?.offsetHeight ?? 0
    setContentHeight(windowHeight - headerHeight - footerHeight)
  }, [headerRef, footerRef, windowHeight])

  return {
    texts: {},
    states: {
      step,
      isPurchase,
      toastText,
      isLoading,
      isOpen,
      frameUrl,
      isComplete,
      isInComplete,
      contentHeight,
      windowHeight,
      headerRef,
      footerRef,
      paymentList,
      ticketSeq,
      isToastOpen,
      isErrorToast
    },
    setters: {
      setIsLoading,
      setIsComplete,
      setIsInComplete,
      setIsToastOpen,
      setIsErrorToast
    },
    handlers: {
      onClosePaymentModal,
      onClickPayment,
      onClickRePayment,
      radioHandler,
      onClickShowTicketBtn,
      onClickHeaderBackBtn
    }
  }
}

export default useParkinglotPurchaseViewModel
